'use client'

import React, { useEffect, useState } from 'react'
import { OffersOffer } from '@/services/dataTypes'
import { filterOfferBanners, getBannerDimensions } from '@/constants/hooks'
import { apiCategoryOfferBanners } from '@/apis/user'
import Banner from './Banner'
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react'

interface Props {
    bannerResponse: OffersOffer[]
    domain: string
    mer_slug: string
    slug_type: string
    categoryId: string
    companyId: string
}

const VerticalCategoryOfferBanner = ({ bannerResponse, domain, mer_slug, slug_type, categoryId, companyId }: Props) => {
    const [banners, setBanners] = useState<OffersOffer[]>([])
    const [page, setPage] = useState(2)
    const [hasMore, setHasMore] = useState(true)
    const [loading, setLoading] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)

    useEffect(() => {
        setBanners(filterOfferBanners(bannerResponse, 50, 2000, 65, 2000))
    }, [bannerResponse])

    const loadMore = async () => {
        if (loading || !hasMore) return
        setLoading(true)
        try {
            const res = await apiCategoryOfferBanners(categoryId, companyId, page)
            const newBanners = filterOfferBanners(res.data?.offers || [], 50, 2000, 65, 2000)
            setBanners(prev => [...prev, ...newBanners])
            if (res?.data?.pagination?.next_page) {
                setPage(prev => prev + 1)
                setIsExpanded(true)
            } else {
                setHasMore(false)
            }
        } catch (e) {
            console.error('Error loading banners', e)
        } finally {
            setLoading(false)
        }
    }

    const showLess = () => {
        setBanners(filterOfferBanners(bannerResponse, 50, 2000, 65, 2000))
        setPage(2)
        setHasMore(true)
        setIsExpanded(false)
    }

    if (!banners?.length) return null

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xs">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 mb-3">Featured Deals</h4>

            <div className="space-y-2">
                {banners.map((offer_data, i) => {
                    const dimension = getBannerDimensions(offer_data)
                    return (
                        <Banner
                            key={i}
                            data={offer_data}
                            height={dimension?.height}
                            width={dimension?.width}
                            mer_slug={mer_slug}
                            slug_type={slug_type}
                            domain={domain}
                        />
                    )
                })}
            </div>

            <div className="flex items-center justify-between mt-3 gap-2">
                {hasMore && (
                    <button
                        onClick={loadMore}
                        disabled={loading}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold text-[#00a87a] border border-slate-200 bg-slate-50 rounded-xl hover:bg-[#071822] hover:text-white transition-all disabled:opacity-50 cursor-pointer shadow-2xs"
                    >
                        {loading ? <Loader2 size={12} className="animate-spin" /> : <ChevronDown size={12} />}
                        {loading ? 'Loading...' : 'Show More'}
                    </button>
                )}
                {isExpanded && (
                    <button
                        onClick={showLess}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold text-slate-600 border border-slate-200 bg-white rounded-xl hover:bg-slate-50 transition-all cursor-pointer shadow-2xs"
                    >
                        <ChevronUp size={12} /> Show Less
                    </button>
                )}
            </div>
        </div>
    )
}

export default VerticalCategoryOfferBanner
